import { useState, useRef, useEffect } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { chatWithGemini, analyzeImageWithGemini, getGeminiApiKey } from "@/lib/gemini-service";
import {
  createVideoCloneRequest,
  getUserVideoCloneRequests,
  subscribeToVideoCloneRequests,
  type VideoCloneRequest
} from "@/lib/video-clone-service";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { useNavigate, useParams } from "react-router-dom";
import GuestOverlay from "@/components/auth/GuestOverlay";
import {
  searchFAQ,
  recordFAQMatch,
  getOrCreateConversation,
  logChatMessage
} from "@/lib/faq-service";
import {
  MessageCircle,
  Image as ImageIcon,
  Video,
  Send,
  Upload,
  Link as LinkIcon,
  Sparkles,
  Bot,
  User,
  Loader2,
  Clock,
  CheckCircle,
  Coins,
  XCircle
} from "lucide-react";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const AIChat = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const { userCredits, deductCredits, createLog, updateLogStatus, refundCredits } = useGeneration();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(tab || "chat");
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      role: "assistant",
      content: t(
        "สวัสดีครับ! ผมเป็น AI ผู้เชี่ยวชาญด้าน Viral Content สำหรับ Photo และ Video ยินดีช่วยเหลือคุณครับ 🎬✨\n\nคุณสามารถถามเกี่ยวกับ:\n- เทคนิคการทำ Viral Content\n- ไอเดีย Photo/Video\n- Prompt สำหรับสร้างภาพ\n- กลยุทธ์ Content Marketing",
        "Hello! I'm an AI expert in Viral Content for Photos and Videos. Happy to help you! 🎬✨\n\nYou can ask about:\n- Viral Content techniques\n- Photo/Video ideas\n- Image generation prompts\n- Content Marketing strategies"
      ),
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Clone Image state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [generatedPrompts, setGeneratedPrompts] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageHistory, setImageHistory] = useState<Array<{
    id: string;
    imagePreview: string;
    fileName: string;
    analyzedAt: Date;
    prompts: string[];
  }>>([
    {
      id: '1',
      imagePreview: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200',
      fileName: 'product-watch.jpg',
      analyzedAt: new Date(Date.now() - 86400000),
      prompts: [
        'Minimalist product shot of luxury watch on marble surface, soft shadows, high-end brand aesthetic',
        'Close-up macro photography of watch mechanism, shallow DOF, metallic textures',
        'Lifestyle shot with watch on wrist, natural outdoor lighting, adventure mood'
      ]
    },
    {
      id: '2',
      imagePreview: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200',
      fileName: 'headphones.jpg',
      analyzedAt: new Date(Date.now() - 3600000 * 3),
      prompts: [
        'Sleek headphones floating with dynamic lighting, neon accents, tech aesthetic',
        'Product photography on gradient background, soft reflections, premium feel',
        'Action shot with headphones in use, urban environment, lifestyle brand'
      ]
    }
  ]);

  // Clone Video state
  const [videoUrl, setVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoDescription, setVideoDescription] = useState("");
  const [isSubmittingVideo, setIsSubmittingVideo] = useState(false);
  const [videoRequests, setVideoRequests] = useState<Array<{
    id: string;
    url: string;
    fileName?: string;
    description: string;
    status: 'pending' | 'processing' | 'completed' | 'rejected';
    submittedAt: Date;
    completedAt?: Date;
    resultPrompts?: string[];
    adminResponse?: string;
  }>>([]);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const pendingQueueCount = videoRequests.filter(r => r.status === 'pending' || r.status === 'processing').length;

  // Redirect if not logged in - REMOVED to allow guest preview
  useEffect(() => {
    // if (!authLoading && !user) {
    //   navigate("/login");
    // }
  }, [user, authLoading, navigate]);

  // Initialize conversation when user is loaded
  useEffect(() => {
    if (user?.id && !conversationId) {
      getOrCreateConversation(user.id).then(setConversationId);
    }
  }, [user, conversationId]);

  // Handle tab from URL
  useEffect(() => {
    if (tab && ["chat", "clone-img", "clone-vdo"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Scroll to top on mount or tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  // Load video clone requests from database
  useEffect(() => {
    if (user?.id) {
      loadVideoCloneRequests();
    }
  }, [user?.id]);

  // Subscribe to real-time updates for video clone requests
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToVideoCloneRequests(user.id, (updatedRequest) => {
      setVideoRequests(prev => {
        const existing = prev.findIndex(r => r.id === updatedRequest.id);
        if (existing >= 0) {
          const newRequests = [...prev];
          newRequests[existing] = {
            id: updatedRequest.id,
            url: updatedRequest.video_url || '',
            fileName: updatedRequest.video_file_name || undefined,
            description: updatedRequest.description,
            status: updatedRequest.status,
            submittedAt: new Date(updatedRequest.submitted_at),
            completedAt: updatedRequest.completed_at ? new Date(updatedRequest.completed_at) : undefined,
            resultPrompts: updatedRequest.result_prompts || undefined,
            adminResponse: updatedRequest.admin_response || undefined
          };

          // Show Notification if request was completed
          if (updatedRequest.status === 'completed' && prev[existing]?.status !== 'completed') {
            toast({
              title: t("งานของคุณเสร็จแล้ว!", "Your request is completed!"),
              description: t("Admin ตอบกลับคำขอ Clone VDO แล้ว", "Admin has responded to your Clone VDO request"),
            });
            refreshProfile();
          }

          return newRequests;
        } else {
          return [...prev, {
            id: updatedRequest.id,
            url: updatedRequest.video_url || '',
            fileName: updatedRequest.video_file_name || undefined,
            description: updatedRequest.description,
            status: updatedRequest.status,
            submittedAt: new Date(updatedRequest.submitted_at),
            completedAt: updatedRequest.completed_at ? new Date(updatedRequest.completed_at) : undefined,
            resultPrompts: updatedRequest.result_prompts || undefined,
            adminResponse: updatedRequest.admin_response || undefined
          }];
        }
      });
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  const loadVideoCloneRequests = async () => {
    if (!user?.id) return;

    const requests = await getUserVideoCloneRequests(user.id);
    setVideoRequests(requests.map(req => ({
      id: req.id,
      url: req.video_url || '',
      fileName: req.video_file_name || undefined,
      description: req.description,
      status: req.status,
      submittedAt: new Date(req.submitted_at),
      completedAt: req.completed_at ? new Date(req.completed_at) : undefined,
      resultPrompts: req.result_prompts || undefined,
      adminResponse: req.admin_response || undefined
    })));
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/ai-chat/${value}`);
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !user?.id) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: chatInput,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput("");
    setIsSending(true);

    try {
      // Ensure conversation exists
      let convId = conversationId;
      if (!convId) {
        convId = await getOrCreateConversation(user.id);
        setConversationId(convId);
      }

      if (!convId) {
        throw new Error("Failed to create conversation");
      }

      // Log user message
      await logChatMessage(convId, user.id, "user", currentInput);

      // 1. Check FAQ first (ประหยัด credits!)
      const faqMatch = await searchFAQ(currentInput, 0.6);

      if (faqMatch) {
        // ✅ เจอคำตอบใน FAQ - ไม่ต้องใช้ credits!
        const faqAnswer = `💡 ${t("พบคำตอบในคลังความรู้", "Found answer in knowledge base")}:\n\n${faqMatch.faq.answer}`;

        // Log assistant message with FAQ
        await logChatMessage(convId, user.id, "assistant", faqAnswer, 0, true, faqMatch.faq.id);

        // Record FAQ match
        await recordFAQMatch(faqMatch.faq.id, currentInput, user.id, faqMatch.similarity_score);

        const aiResponse: ChatMessage = {
          id: Date.now().toString(),
          role: "assistant",
          content: faqAnswer,
          timestamp: new Date()
        };

        setChatMessages(prev => [...prev, aiResponse]);

        toast({
          title: t("✅ ฟรี! ใช้คำตอบจาก FAQ", "✅ Free! Used FAQ Answer"),
          description: t("ประหยัด 5 credits จากคลังความรู้", "Saved 5 credits from knowledge base")
        });

        setIsSending(false);
        return;
      }

      // 2. No FAQ match - ต้องใช้ AI (หัก credits)
      if (userCredits < 5) {
        toast({
          title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
          description: t("ต้องการ 5 credits สำหรับการถาม 1 คำถาม", "Requires 5 credits per question"),
          variant: "destructive"
        });
        setChatInput(currentInput);
        setIsSending(false);
        return;
      }

      const apiKey = await getGeminiApiKey();
      if (!apiKey) {
        toast({
          title: t("ไม่พบ API Key", "API Key not found"),
          description: t("กรุณาตั้งค่า Gemini API Key ในหน้า Admin > Settings", "Please configure Gemini API Key in Admin > Settings"),
          variant: "destructive"
        });
        setChatInput(currentInput);
        setIsSending(false);
        return;
      }

      // Deduct credits FIRST - this ensures valid transaction log
      const deduction = await deductCredits(5, "photo" as any, "gemini-chat", currentInput);
      if (!deduction.success) {
        setChatInput(currentInput);
        setIsSending(false);
        return; // Toast handled by deductCredits
      }

      const logId = deduction.logId;

      // Prepare history for Gemini
      const messageHistory = chatMessages
        .filter(m => m.id !== "1")
        .map(m => ({
          role: m.role,
          content: m.content
        }));

      messageHistory.push({ role: "user", content: currentInput });

      try {
        const responseText = await chatWithGemini(messageHistory, apiKey);

        // Update log to completed
        if (logId) {
          await updateLogStatus(logId, "completed", "chat-response");
        }

        // Log assistant message
        await logChatMessage(convId, user.id, "assistant", responseText, 5);

        const aiResponse: ChatMessage = {
          id: Date.now().toString(),
          role: "assistant",
          content: responseText,
          timestamp: new Date()
        };

        setChatMessages(prev => [...prev, aiResponse]);
        await refreshProfile();

        toast({
          title: t("หัก 5 Credits", "Deducted 5 Credits"),
          description: t("ใช้ AI Chat สำเร็จ", "AI Chat used successfully")
        });
      } catch (geminiError) {
        // If chat fails, refund
        if (logId) {
          await refundCredits(logId);
        }
        throw geminiError;
      }
    } catch (error: any) {
      console.error("Chat Error:", error);
      toast({
        title: t("เกิดข้อผิดพลาด", "Error occurred"),
        description: error.message || t("ไม่สามารถดำเนินการได้", "Failed to process"),
        variant: "destructive"
      });
      setChatInput(currentInput);
    } finally {
      setIsSending(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setGeneratedPrompts([]);
    }
  };

  const handleAnalyzeImage = async () => {
    if (!selectedImage || !imagePreview) return;

    if (userCredits < 4) {
      toast({
        title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
        description: t("ต้องการ 4 credits สำหรับ Clone Image", "Requires 4 credits for Clone Image"),
        variant: "destructive"
      });
      return;
    }

    const apiKey = await getGeminiApiKey();
    if (!apiKey) {
      toast({
        title: t("ไม่พบ API Key", "API Key not found"),
        description: t("กรุณาตั้งค่า Gemini API Key ในหน้า Admin > Settings", "Please configure Gemini API Key in Admin > Settings"),
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);

    let logId: string | undefined;

    try {
      // 1. Deduct credits FIRST - Creates the single authoritative log
      const deduction = await deductCredits(4, "photo" as any, "gemini-vision", "Image Analysis");

      if (!deduction.success) return; // Toast handled by deductCredits

      logId = deduction.logId;

      const prompt = "Analyze this image and generate 5 very detailed prompts that can recreate this style, subject, and lighting in an AI image generator like stable diffusion or flux. Return the result as a simple JSON array of 5 strings. Example: [\"prompt 1\", \"prompt 2\", ...]";

      const response = await analyzeImageWithGemini(imagePreview, prompt, apiKey, true);

      let newPrompts: string[] = [];
      try {
        newPrompts = JSON.parse(response);
        if (!Array.isArray(newPrompts)) {
          newPrompts = [response]; // Fallback if it's not an array
        }
      } catch (e) {
        console.error("JSON Parse Error:", e);
        // Fallback to old parsing logic if JSON fails
        newPrompts = response
          .split(/\d\.\s+/)
          .filter(p => p.trim().length > 10)
          .map(p => p.trim())
          .slice(0, 5);
        if (newPrompts.length === 0) newPrompts = [response];
      }

      if (logId) {
        await updateLogStatus(logId, "completed", "vision-response");
      }

      setGeneratedPrompts(newPrompts);

      // Add to history
      const newHistoryItem = {
        id: `img-${Date.now()}`,
        imagePreview: imagePreview || '',
        fileName: selectedImage.name,
        analyzedAt: new Date(),
        prompts: newPrompts.length > 0 ? newPrompts : [response]
      };
      setImageHistory(prev => [newHistoryItem, ...prev]);
      await refreshProfile();

      toast({
        title: t("หัก 4 Credits", "Deducted 4 Credits"),
        description: t("วิเคราะห์ภาพสำเร็จ!", "Image analyzed successfully!")
      });
    } catch (error: any) {
      console.error("Image analysis error:", error);

      // Auto-refund on failure
      if (logId) {
        await refundCredits(logId);
        await updateLogStatus(logId, "failed");
      }

      toast({
        title: t("เกิดข้อผิดพลาด", "Error occurred"),
        description: error.message || t("ไม่สามารถวิเคราะห์ภาพได้", "Failed to analyze image"),
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmitVideo = async () => {
    if (!videoUrl.trim() && !videoFile) {
      toast({
        title: t("กรุณาใส่ URL หรืออัพโหลดวิดีโอ", "Please enter URL or upload video"),
        variant: "destructive"
      });
      return;
    }

    if (userCredits < 110) {
      toast({
        title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
        description: t("ต้องการ 110 credits สำหรับ Clone Video", "Requires 110 credits for Clone Video"),
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: t("กรุณาเข้าสู่ระบบ", "Please login"),
        variant: "destructive"
      });
      return;
    }

    setIsSubmittingVideo(true);

    try {
      // Create request in database (this will also deduct credits)
      const requestId = await createVideoCloneRequest(
        user.id,
        videoUrl || null,
        videoFile?.name || null,
        videoDescription || t("ไม่ระบุรายละเอียด", "No description provided")
      );

      if (!requestId) {
        throw new Error("Failed to create video clone request");
      }

      // Add to local state temporarily
      const newRequest = {
        id: requestId,
        url: videoUrl || '',
        fileName: videoFile?.name,
        description: videoDescription || t("ไม่ระบุรายละเอียด", "No description provided"),
        status: 'pending' as const,
        submittedAt: new Date()
      };
      setVideoRequests(prev => [newRequest, ...prev]);

      setVideoUrl("");
      setVideoFile(null);
      setVideoDescription("");
      await refreshProfile();

      toast({
        title: t("ส่งคำขอสำเร็จ!", "Request submitted successfully!"),
        description: t("Admin จะตรวจสอบและตอบกลับภายใน 24-48 ชั่วโมง", "Admin will review and respond within 24-48 hours"),
      });
    } catch (error: any) {
      console.error("Video submission error:", error);
      toast({
        title: t("เกิดข้อผิดพลาด", "Error occurred"),
        description: error.message || t("ไม่สามารถดำเนินการได้", "Failed to process request"),
        variant: "destructive"
      });
    } finally {
      setIsSubmittingVideo(false);
    }
  };

  const copyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    toast({
      title: t("คัดลอกแล้ว!", "Copied!"),
      description: t("Prompt ถูกคัดลอกไปยัง Clipboard", "Prompt copied to clipboard")
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="container mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
            <Bot className="h-4 w-4" />
            <span className="text-sm font-medium">AI Assistant</span>
          </div>
          <h1 className="text-4xl font-heading font-bold mb-4">
            <span className="text-gradient">AI Chat</span> - {t("ผู้เชี่ยวชาญ Viral Content", "Viral Content Expert")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("ผู้ช่วย AI ที่เชี่ยวชาญด้าน Photo & Video Viral Content พร้อมช่วยคุณสร้างคอนเทนต์ที่โดนใจ", "AI assistant expert in Photo & Video Viral Content, ready to help you create engaging content")}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Coins className="h-5 w-5 text-primary" />
            <span className="font-semibold">{(userCredits || 0).toLocaleString()} {t("เครดิต", "Credits")}</span>
          </div>
        </div>

        {/* Main Content */}
        <Card variant="glass" className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full grid grid-cols-3 p-1 bg-muted/50">
              <TabsTrigger value="chat" className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">AI Chat</span>
                <Badge variant="secondary" className="text-xs">5 เครดิต</Badge>
              </TabsTrigger>
              <TabsTrigger value="clone-img" className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Clone Img</span>
                <Badge variant="secondary" className="text-xs">4 เครดิต</Badge>

              </TabsTrigger>
              <TabsTrigger value="clone-vdo" className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                <span className="hidden sm:inline">Clone VDO</span>
                <Badge variant="secondary" className="text-xs">110 เครดิต</Badge>
              </TabsTrigger>
            </TabsList>

            {/* AI Chat Tab */}
            <TabsContent value="chat" className="p-0">
              <div className="flex flex-col h-[600px]">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                        }`}>
                        {msg.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                        }`}>
                        <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        <span className="text-xs opacity-60 mt-1 block">
                          {msg.timestamp.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  ))}
                  {isSending && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div className="bg-muted rounded-2xl px-4 py-3">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="p-4 border-t border-border relative">
                  {!user && (
                    <GuestOverlay
                      title={t("เข้าสู่ระบบเพื่อพูดคุยกับ AI", "Login to Chat with AI")}
                      description={t("เริ่มต้นคุยกับ AI ผู้เชี่ยวชาญของเราได้ทันทีหลังสมัครสมาชิก", "Start chatting with our expert AI immediately after registering.")}
                      showIcon={false}
                    />
                  )}
                  <div className={`flex gap-2 ${!user ? "opacity-20 blur-[1px] pointer-events-none" : ""}`}>
                    <Input
                      placeholder={t("พิมพ์คำถามของคุณ... (5 credits/คำถาม)", "Type your question... (5 credits/question)")}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                      disabled={isSending || !user}
                    />
                    <Button onClick={handleSendMessage} disabled={isSending || !chatInput.trim() || !user}>
                      {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Clone Image Tab */}
            <TabsContent value="clone-img" className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Upload Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Upload className="h-5 w-5 text-primary" />
                    {t("อัพโหลดภาพต้นแบบ", "Upload Reference Image")}
                  </h3>

                  <div
                    className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview ? (
                      <div className="space-y-4">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <p className="text-sm text-muted-foreground">{selectedImage?.name}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground" />
                        <p className="text-muted-foreground">{t("คลิกเพื่ออัพโหลดภาพ", "Click to upload image")}</p>
                        <p className="text-xs text-muted-foreground">PNG, JPG, WEBP (Max 10MB)</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />

                  <div className="relative mt-4">
                    {!user && (
                      <GuestOverlay
                        title={t("วิเคราะห์และ Clone ภาพ", "Analyze & Clone Image")}
                        description={t("สมัครสมาชิกเพื่อเข้าถึงฟีเจอร์วิเคราะห์ภาพและรับ Prompts เลียนแบบสไตล์", "Register to access image analysis and get style-mimicking prompts.")}
                        showIcon={false}
                      />
                    )}
                    <div className={!user ? "opacity-20 blur-[1px] pointer-events-none" : ""}>
                      <Button
                        className="w-full"
                        variant="glow"
                        onClick={handleAnalyzeImage}
                        disabled={!selectedImage || isAnalyzing || !user}
                      >
                        {isAnalyzing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t("กำลังวิเคราะห์...", "Analyzing...")}
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            {t("วิเคราะห์ภาพ (4 Credits)", "Analyze Image (4 Credits)")}

                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    {t("Prompts ที่สร้างได้", "Generated Prompts")}
                  </h3>

                  {generatedPrompts.length > 0 ? (
                    <div className="space-y-3 max-h-[280px] overflow-y-auto">
                      {generatedPrompts.map((prompt, index) => (
                        <Card key={index} className="p-3 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => copyPrompt(prompt)}>
                          <div className="flex items-start gap-2">
                            <Badge variant="outline" className="shrink-0">#{index + 1}</Badge>
                            <p className="text-sm text-muted-foreground line-clamp-3">{prompt}</p>
                          </div>
                          <p className="text-xs text-primary mt-2">{t("คลิกเพื่อคัดลอก", "Click to copy")}</p>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center bg-muted/30 rounded-xl">
                      <div className="text-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>{t("อัพโหลดภาพเพื่อรับ 5 Prompts", "Upload image to get 5 Prompts")}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Image History Section */}
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  {t("ประวัติการวิเคราะห์ภาพ", "Image Analysis History")}
                  <Badge variant="secondary">{imageHistory.length} {t("รายการ", "items")}</Badge>
                </h3>

                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {imageHistory.map((item) => (
                    <Card key={item.id} className="p-4">
                      <div className="flex gap-4">
                        {/* Thumbnail */}
                        <div className="shrink-0">
                          <img
                            src={item.imagePreview}
                            alt={item.fileName}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-sm truncate">{item.fileName}</p>
                            <span className="text-xs text-muted-foreground">
                              {item.analyzedAt.toLocaleDateString("th-TH")} {item.analyzedAt.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>

                          {/* Prompts */}
                          <div className="space-y-1">
                            {item.prompts.map((prompt, i) => (
                              <div
                                key={i}
                                className="text-xs text-muted-foreground bg-muted p-2 rounded cursor-pointer hover:bg-muted/80 transition-colors"
                                onClick={() => copyPrompt(prompt)}
                              >
                                <span className="text-primary font-medium">#{i + 1}</span> {prompt}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}

                  {imageHistory.length === 0 && (
                    <div className="h-[150px] flex items-center justify-center bg-muted/30 rounded-xl">
                      <div className="text-center text-muted-foreground">
                        <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{t("ยังไม่มีประวัติ", "No history yet")}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Clone Video Tab */}
            <TabsContent value="clone-vdo" className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Submit Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Video className="h-5 w-5 text-primary" />
                    {t("ส่งวิดีโอต้นแบบ", "Submit Reference Video")}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t("URL วิดีโอ (TikTok, YouTube, etc.)", "Video URL (TikTok, YouTube, etc.)")}</label>
                      <div className="flex gap-2">
                        <LinkIcon className="h-5 w-5 text-muted-foreground mt-3" />
                        <Input
                          placeholder="https://..."
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="text-center text-muted-foreground text-sm">{t("หรือ", "or")}</div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">{t("อัพโหลดไฟล์วิดีโอ", "Upload Video File")}</label>
                      <div
                        className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        {videoFile ? (
                          <p className="text-sm">{videoFile.name}</p>
                        ) : (
                          <>
                            <Video className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">{t("คลิกเพื่ออัพโหลดวิดีโอ", "Click to upload video")}</p>
                          </>
                        )}
                      </div>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">{t("รายละเอียดเพิ่มเติม (Optional)", "Additional Details (Optional)")}</label>
                      <Textarea
                        placeholder={t("อธิบายสิ่งที่ต้องการ Clone หรือ สไตล์ที่สนใจ...", "Describe what you want to clone or style you're interested in...")}
                        value={videoDescription}
                        onChange={(e) => setVideoDescription(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="relative">
                      {!user && (
                        <GuestOverlay
                          title={t("วิเคราะห์วิดีโอระดับโปร", "Pro Video Analysis")}
                          description={t("ปลดล็อคการวิเคราะห์วิดีโอเพื่อสร้างผลงานระดับโลกตามแบบที่คุณชอบ", "Unlock video analysis to create world-class content based on your style preferences.")}
                          showIcon={false}
                        />
                      )}
                      <div className={!user ? "opacity-20 blur-[1px] pointer-events-none" : ""}>
                        <Button
                          className="w-full"
                          variant="glow"
                          onClick={handleSubmitVideo}
                          disabled={isSubmittingVideo || (!videoUrl && !videoFile) || !user}
                        >
                          {isSubmittingVideo ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {t("กำลังส่ง...", "Submitting...")}
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              {t("ส่งให้ทีมงานตรวจสอบ (110 Credits)", "Submit for Review (110 Credits)")}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground text-center">
                      {t("ทีมงานจะตรวจสอบและตอบกลับภายใน 24-48 ชั่วโมง", "Team will review and respond within 24-48 hours")}
                    </p>
                  </div>
                </div>

                {/* Request History */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    {t("ประวัติคำขอ", "Request History")}
                    {pendingQueueCount > 0 && (
                      <Badge variant="secondary">{pendingQueueCount} {t("รอดำเนินการ", "pending")}</Badge>
                    )}
                  </h3>

                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {videoRequests.map((req) => (
                      <Card key={req.id} className={`p-4 ${req.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                        req.status === "rejected" ? "border-red-500/50 bg-red-500/5" :
                          req.status === "processing" ? "border-yellow-500/50 bg-yellow-500/5" : ""
                        }`}>
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={
                            req.status === "completed" ? "default" :
                              req.status === "rejected" ? "destructive" :
                                req.status === "processing" ? "secondary" : "outline"
                          }>
                            {req.status === "completed" && <CheckCircle className="h-3 w-3 mr-1" />}
                            {req.status === "rejected" && <XCircle className="h-3 w-3 mr-1" />}
                            {req.status === "processing" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            {req.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                            {req.status === "completed" ? t("เสร็จแล้ว", "Completed") :
                              req.status === "rejected" ? t("ถูกปฏิเสธ", "Rejected") :
                                req.status === "processing" ? t("กำลังตรวจสอบ", "Processing") : t("รอคิว", "Pending")}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {req.submittedAt.toLocaleDateString("th-TH")} {req.submittedAt.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>

                        {/* URL/File Info */}
                        <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                          {req.url ? (
                            <div className="flex items-center gap-2">
                              <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                              <a
                                href={req.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline truncate"
                              >
                                {req.url}
                              </a>
                            </div>
                          ) : req.fileName && (
                            <div className="flex items-center gap-2">
                              <Video className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground">{req.fileName}</span>
                            </div>
                          )}
                          {req.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{req.description}</p>
                          )}
                        </div>

                        {/* Admin Response (for rejected/completed) */}
                        {req.adminResponse && (
                          <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-sm">
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                              {t("ข้อความจาก Admin:", "Message from Admin:")}
                            </p>
                            <p className="text-xs text-muted-foreground">{req.adminResponse}</p>
                          </div>
                        )}

                        {/* Result Prompts */}
                        {req.status === "completed" && req.resultPrompts && (
                          <div className="mt-3 space-y-2">
                            <p className="text-sm font-medium text-green-600 dark:text-green-400">{t("Prompts ที่ได้:", "Received Prompts:")}</p>
                            {req.resultPrompts.map((prompt, i) => (
                              <div
                                key={i}
                                className="text-xs text-muted-foreground bg-muted p-2 rounded cursor-pointer hover:bg-muted/80 transition-colors"
                                onClick={() => copyPrompt(prompt)}
                              >
                                <div className="flex items-start gap-2">
                                  <Badge variant="outline" className="shrink-0 text-[10px]">#{i + 1}</Badge>
                                  <span>{prompt}</span>
                                </div>
                                <p className="text-[10px] text-primary mt-1">{t("คลิกเพื่อคัดลอก", "Click to copy")}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}

                    {videoRequests.length === 0 && (
                      <div className="h-[200px] flex items-center justify-center bg-muted/30 rounded-xl">
                        <div className="text-center text-muted-foreground">
                          <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t("ยังไม่มีคำขอ", "No requests yet")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AIChat;
